import {
  Calendar,
  Clock,
  Eye,
  Facebook,
  Heart,
  Linkedin,
  Link as LinkIcon,
  Share2,
  ThumbsDown,
  ThumbsUp,
  Twitter,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AppNavbar from "@/components/common/AppNavbar";
import { subscribeToNewsletter } from "@/services/landing/newsletterService";
import {
  fetchUpdates,
  getInteractionStatus,
  incrementView,
  toggleDislike,
  toggleLike,
  UpdateItem,
} from "@/services/updatesService";

export default function UpdateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [update, setUpdate] = useState<null | UpdateItem>(null);
  const [relatedUpdates, setRelatedUpdates] = useState<UpdateItem[]>([]);
  const [popularUpdates, setPopularUpdates] = useState<UpdateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);
  const [views, setViews] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [interactionLoading, setInteractionLoading] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterName, setNewsletterName] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState<null | {
    text: string;
    type: "error" | "success";
  }>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUpdate = async () => {
      try {
        const data = await fetchUpdates();
        const found = data.find((u) => String(u.id) === String(id));
        setUpdate(found || null);

        if (found) {
          // Get related updates (same type, excluding current)
          const related = data
            .filter((u) => u.type === found.type && u.id !== found.id)
            .slice(0, 3);
          setRelatedUpdates(related);

          // Get popular updates (different from current)
          const popular = data.filter((u) => u.id !== Number(id)).slice(0, 4);
          setPopularUpdates(popular);

          // Increment view count
          const viewData = await incrementView(found.id);
          setViews(viewData.views_count);

          // Get interaction status
          const interactionStatus = await getInteractionStatus(found.id);
          setLiked(interactionStatus.has_liked);
          setDisliked(interactionStatus.has_disliked);
          setLikeCount(interactionStatus.likes_count ?? 0);
          setDislikeCount(interactionStatus.dislikes_count ?? 0);
        }
      } catch (error) {
        console.error("Failed to load update:", error);
      } finally {
        setLoading(false);
      }
    };

    loadUpdate();
  }, [id]);

  const handleLike = async () => {
    if (!update || interactionLoading) return;

    setInteractionLoading(true);
    try {
      const response = await toggleLike(update.id);

      if (response.success) {
        setLikeCount(response.likes_count);
        setDislikeCount(response.dislikes_count);
        setLiked(response.action === "liked");
        setDisliked(false);
      }
    } catch (error) {
      console.error("Failed to toggle like:", error);
    } finally {
      setInteractionLoading(false);
    }
  };

  const handleDislike = async () => {
    if (!update || interactionLoading) return;

    setInteractionLoading(true);
    try {
      const response = await toggleDislike(update.id);

      if (response.success) {
        setLikeCount(response.likes_count);
        setDislikeCount(response.dislikes_count);
        setDisliked(response.action === "disliked");
        setLiked(false);
      }
    } catch (error) {
      console.error("Failed to toggle dislike:", error);
    } finally {
      setInteractionLoading(false);
    }
  };

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = update?.title || "";
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    };

    if (platform === "copy") {
      navigator.clipboard.writeText(url);
      alert("Link copied to clipboard!");
    } else if (shareUrls[platform as keyof typeof shareUrls]) {
      window.open(shareUrls[platform as keyof typeof shareUrls], "_blank");
    }
    setShowShareMenu(false);
  };

  const calculateReadTime = (text: string) => {
    const wordsPerMinute = 200;
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  const handleNewsletterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail) {
      setNewsletterMessage({
        text: "Please enter your email address",
        type: "error",
      });
      return;
    }

    setNewsletterLoading(true);
    setNewsletterMessage(null);

    try {
      const response = await subscribeToNewsletter({
        email: newsletterEmail,
        ...(newsletterName && { name: newsletterName }),
      });

      if (response.success) {
        setNewsletterMessage({
          text:
            response.message || "Successfully subscribed to our newsletter!",
          type: "success",
        });
        setNewsletterEmail("");
        setNewsletterName("");
      }
    } catch (error: any) {
      setNewsletterMessage({
        text:
          error.response?.data?.message ||
          "Failed to subscribe. Please try again.",
        type: "error",
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  // Modern shimmer for loading
  function ModernShimmer({
    className = "",
    height,
    width,
  }: {
    className?: string;
    height: string;
    width: string;
  }) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl shadow-sm ${className}`}
        style={{ height, width }}
      >
        <div
          className="animate-shimmer absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-base-200 dark:via-base-100 dark:to-base-200"
          style={{ backgroundSize: "200% 100%" }}
        />
      </div>
    );
  }

  if (
    typeof document !== "undefined" &&
    !document.getElementById("modern-shimmer-style")
  ) {
    const style = document.createElement("style");
    style.id = "modern-shimmer-style";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 1.5s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <>
      <AppNavbar />
      <div className="min-h-screen bg-base-200 pt-16">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            className="btn btn-ghost btn-sm mb-4 md:mb-6"
            onClick={() => navigate(-1)}
          >
            ← Back to Updates
          </button>

          {loading ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <div className="flex flex-col gap-4">
                  <ModernShimmer height="3rem" width="100%" />
                  <ModernShimmer height="400px" width="100%" />
                  <ModernShimmer height="8rem" width="100%" />
                </div>
              </div>
              <div className="hidden lg:block">
                <ModernShimmer height="500px" width="100%" />
              </div>
            </div>
          ) : update ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <article className="rounded-2xl bg-base-100 shadow-lg">
                  {/* Header Image */}
                  {update.image && (
                    <div className="relative h-64 overflow-hidden rounded-t-2xl sm:h-80 md:h-96">
                      <img
                        alt={update.title}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        src={
                          update.image.startsWith("http")
                            ? update.image
                            : `${import.meta.env.VITE_API_STORAGE_URL}${update.image}`
                        }
                      />
                    </div>
                  )}

                  <div className="p-4 sm:p-6 md:p-8">
                    {/* Category Badge */}
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <span className="badge badge-primary badge-lg uppercase">
                        {update.type}
                      </span>
                      <div className="flex items-center gap-1 text-sm text-base-content/70">
                        <Eye size={16} />
                        <span>{views.toLocaleString()} views</span>
                      </div>
                    </div>

                    {/* Title */}
                    <h1 className="mb-4 text-2xl font-bold leading-tight sm:text-3xl md:text-4xl lg:text-5xl">
                      {update.title}
                    </h1>

                    {/* Meta Information */}
                    <div className="mb-6 flex flex-wrap items-center gap-3 border-b border-base-300 pb-4 text-sm text-base-content/70 sm:gap-4 md:text-base">
                      {update.subtitle && (
                        <div className="flex items-center gap-2">
                          <User size={18} />
                          <span>{update.subtitle}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar size={18} />
                        <span>
                          {new Date(update.date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={18} />
                        <span>
                          {calculateReadTime(update.description)} min read
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="prose prose-lg mb-8 max-w-none">
                      <p className="text-base leading-relaxed text-base-content/90 sm:text-lg md:text-xl">
                        {update.description}
                      </p>
                    </div>

                    {/* Engagement Section */}
                    <div className="border-t border-base-300 pt-6">
                      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-2 sm:gap-4">
                          {/* Like Button */}
                          <button
                            className={`btn btn-sm sm:btn-md ${liked ? "btn-primary" : "btn-outline"} flex items-center gap-2`}
                            disabled={interactionLoading}
                            onClick={handleLike}
                          >
                            {liked ? (
                              <Heart className="fill-current" size={18} />
                            ) : (
                              <ThumbsUp size={18} />
                            )}
                            <span className="hidden sm:inline">Like</span>
                            <span className="font-semibold">{likeCount}</span>
                          </button>

                          {/* Dislike Button */}
                          <button
                            className={`btn btn-sm sm:btn-md ${disliked ? "btn-error" : "btn-outline"} flex items-center gap-2`}
                            disabled={interactionLoading}
                            onClick={handleDislike}
                          >
                            <ThumbsDown size={18} />
                            <span className="hidden sm:inline">Dislike</span>
                            <span className="font-semibold">
                              {dislikeCount}
                            </span>
                          </button>
                        </div>

                        {/* Share Button */}
                        <div className="relative">
                          <button
                            className="btn btn-outline btn-sm flex items-center gap-2 sm:btn-md"
                            onClick={() => setShowShareMenu(!showShareMenu)}
                          >
                            <Share2 size={18} />
                            <span className="hidden sm:inline">Share</span>
                          </button>

                          {showShareMenu && (
                            <div className="absolute right-0 top-full z-10 mt-2 w-48 rounded-lg bg-base-100 p-2 shadow-xl ring-1 ring-base-300">
                              <button
                                className="flex w-full items-center gap-3 rounded-md px-4 py-2 text-sm hover:bg-base-200"
                                onClick={() => handleShare("facebook")}
                              >
                                <Facebook size={18} />
                                <span>Facebook</span>
                              </button>
                              <button
                                className="flex w-full items-center gap-3 rounded-md px-4 py-2 text-sm hover:bg-base-200"
                                onClick={() => handleShare("twitter")}
                              >
                                <Twitter size={18} />
                                <span>Twitter</span>
                              </button>
                              <button
                                className="flex w-full items-center gap-3 rounded-md px-4 py-2 text-sm hover:bg-base-200"
                                onClick={() => handleShare("linkedin")}
                              >
                                <Linkedin size={18} />
                                <span>LinkedIn</span>
                              </button>
                              <button
                                className="flex w-full items-center gap-3 rounded-md px-4 py-2 text-sm hover:bg-base-200"
                                onClick={() => handleShare("copy")}
                              >
                                <LinkIcon size={18} />
                                <span>Copy Link</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>

                {/* Related Updates - Mobile */}
                {relatedUpdates.length > 0 && (
                  <div className="mt-6 rounded-2xl bg-base-100 p-4 shadow-lg sm:p-6 lg:hidden">
                    <h3 className="mb-4 text-xl font-bold sm:text-2xl">
                      Related Updates
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {relatedUpdates.map((related) => (
                        <div
                          className="card cursor-pointer bg-base-200 shadow-md transition-shadow hover:shadow-xl"
                          key={related.id}
                          onClick={() => navigate(`/updates/${related.id}`)}
                        >
                          {related.image && (
                            <figure className="h-32 sm:h-40">
                              <img
                                alt={related.title}
                                className="h-full w-full object-cover"
                                src={
                                  related.image.startsWith("http")
                                    ? related.image
                                    : `${import.meta.env.VITE_API_STORAGE_URL}${related.image}`
                                }
                              />
                            </figure>
                          )}
                          <div className="card-body p-4">
                            <span className="badge badge-outline badge-sm">
                              {related.type}
                            </span>
                            <h4 className="card-title text-base">
                              {related.title}
                            </h4>
                            <p className="line-clamp-2 text-sm text-base-content/70">
                              {related.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar - Desktop */}
              <div className="hidden space-y-6 lg:block">
                {/* Latest Updates */}
                {popularUpdates.length > 0 && (
                  <div className="rounded-2xl bg-base-100 p-6 shadow-lg">
                    <h3 className="mb-4 text-xl font-bold">Latest Updates</h3>
                    <div className="space-y-3">
                      {popularUpdates.map((popular, index) => (
                        <div
                          className="flex cursor-pointer gap-3 rounded-lg p-2 transition-all hover:bg-base-200"
                          key={popular.id}
                          onClick={() => navigate(`/updates/${popular.id}`)}
                        >
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-content">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="line-clamp-2 text-sm font-semibold">
                              {popular.title}
                            </h4>
                            <div className="mt-1 flex items-center gap-2 text-xs text-base-content/70">
                              <span className="badge badge-xs">
                                {popular.type}
                              </span>
                              <span>
                                {new Date(popular.date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      className="btn btn-outline btn-sm btn-block mt-4"
                      onClick={() => navigate("/pages/about-us/updates")}
                    >
                      View All Updates
                    </button>
                  </div>
                )}

                {/* Related Updates */}
                {relatedUpdates.length > 0 && (
                  <div className="rounded-2xl bg-base-100 p-6 shadow-lg">
                    <h3 className="mb-4 text-xl font-bold">Related Updates</h3>
                    <div className="space-y-4">
                      {relatedUpdates.map((related) => (
                        <div
                          className="cursor-pointer rounded-lg bg-base-200 p-3 transition-all hover:shadow-md"
                          key={related.id}
                          onClick={() => navigate(`/updates/${related.id}`)}
                        >
                          {related.image && (
                            <img
                              alt={related.title}
                              className="mb-2 h-24 w-full rounded-md object-cover"
                              src={
                                related.image.startsWith("http")
                                  ? related.image
                                  : `${import.meta.env.VITE_API_STORAGE_URL}${related.image}`
                              }
                            />
                          )}
                          <span className="badge badge-xs mb-2">
                            {related.type}
                          </span>
                          <h4 className="mb-1 line-clamp-2 text-sm font-semibold">
                            {related.title}
                          </h4>
                          <p className="line-clamp-2 text-xs text-base-content/70">
                            {related.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Newsletter Signup */}
                <div className="rounded-2xl bg-gradient-to-br from-primary to-secondary p-6 text-primary-content shadow-lg transition-colors duration-300 dark:from-primary/90 dark:to-secondary/90">
                  <h3 className="mb-2 text-xl font-bold">Stay Updated</h3>
                  <p className="mb-4 text-sm opacity-90">
                    Subscribe to our newsletter for the latest updates and news
                  </p>
                  <form
                    className="space-y-3"
                    onSubmit={handleNewsletterSubscribe}
                  >
                    {/* Email Input */}
                    <div className="relative">
                      <input
                        className="input input-sm w-full border-primary-content/30 bg-primary-content/10 text-primary-content placeholder-primary-content/50 backdrop-blur-sm transition-all duration-200 focus:border-primary-content focus:bg-primary-content/20 focus:outline-none focus:ring-2 focus:ring-primary-content/30 disabled:opacity-50 dark:border-primary-content/40 dark:bg-primary-content/5"
                        disabled={newsletterLoading}
                        onChange={(e) => setNewsletterEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                        type="email"
                        value={newsletterEmail}
                      />
                    </div>

                    {/* Name Input */}
                    <div className="relative">
                      <input
                        className="input input-sm w-full border-primary-content/30 bg-primary-content/10 text-primary-content placeholder-primary-content/50 backdrop-blur-sm transition-all duration-200 focus:border-primary-content focus:bg-primary-content/20 focus:outline-none focus:ring-2 focus:ring-primary-content/30 disabled:opacity-50 dark:border-primary-content/40 dark:bg-primary-content/5"
                        disabled={newsletterLoading}
                        onChange={(e) => setNewsletterName(e.target.value)}
                        placeholder="Your name (optional)"
                        type="text"
                        value={newsletterName}
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      className="btn btn-sm btn-block bg-primary-content font-semibold text-primary transition-all duration-200 hover:bg-primary-content/90 active:bg-primary-content/75 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary-content/95 dark:text-primary dark:hover:bg-primary-content/85"
                      disabled={newsletterLoading}
                      type="submit"
                    >
                      {newsletterLoading ? (
                        <>
                          <span className="loading loading-spinner loading-sm"></span>
                          <span className="hidden sm:inline">
                            Subscribing...
                          </span>
                          <span className="sm:hidden">Subscribing...</span>
                        </>
                      ) : (
                        "Subscribe"
                      )}
                    </button>
                  </form>

                  {/* Success/Error Message */}
                  {newsletterMessage && (
                    <div
                      className={`animate-in fade-in mt-4 rounded-lg border-l-4 p-3 text-sm transition-all duration-300 ${
                        newsletterMessage.type === "success"
                          ? "border-l-success bg-success/10 text-success dark:bg-success/5"
                          : "border-l-error bg-error/10 text-error dark:bg-error/5"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {newsletterMessage.type === "success" ? (
                          <span className="text-lg">✓</span>
                        ) : (
                          <span className="text-lg">✕</span>
                        )}
                        <span>{newsletterMessage.text}</span>
                      </div>
                    </div>
                  )}

                  {/* Footer Text */}
                  <p className="mt-4 text-center text-xs opacity-75 transition-opacity duration-300">
                    No spam, unsubscribe anytime
                  </p>
                </div>

                {/* Quick Stats */}
                <div className="rounded-2xl bg-base-100 p-6 shadow-lg transition-all duration-300 hover:shadow-xl dark:bg-slate-800">
                  <h3 className="mb-4 text-xl font-bold">Platform Stats</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-base-300 pb-2 dark:border-slate-700">
                      <span className="text-sm text-base-content/70 dark:text-slate-400">
                        Total Updates
                      </span>
                      <span className="text-lg font-bold text-primary">
                        {popularUpdates.length + relatedUpdates.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-base-300 pb-2 dark:border-slate-700">
                      <span className="text-sm text-base-content/70 dark:text-slate-400">
                        Categories
                      </span>
                      <span className="text-lg font-bold text-secondary">
                        {
                          new Set([
                            update?.type,
                            ...popularUpdates.map((u) => u.type),
                            ...relatedUpdates.map((u) => u.type),
                          ]).size
                        }
                      </span>
                    </div>
                    <div className="flex items-center justify-between dark:border-slate-700">
                      <span className="text-sm text-base-content/70 dark:text-slate-400">
                        This Update Views
                      </span>
                      <span className="text-lg font-bold text-accent">
                        {views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex min-h-[50vh] items-center justify-center">
              <div className="text-center">
                <h2 className="mb-2 text-2xl font-bold text-error sm:text-3xl">
                  Update not found
                </h2>
                <p className="mb-4 text-base-content/70">
                  The update you're looking for doesn't exist or has been
                  removed.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate("/pages/about-us/updates")}
                >
                  View All Updates
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
